import { LoadingOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import { message } from 'antd';
import {
  activityGlobalStats,
  ActivityGlobalStatsBody,
  ActivityStatsWithDaily,
  PlatformRegionData,
} from 'services/api/dashboard';
import useActivityContents from './useActivityContents';
import { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import DetailTable from './DetailTable';
import CurrencySelector from './CurrencySelector';
import Filter from './Filter';
import styled from '@emotion/styled';
import PlatformRegionChart from './PlatformRegionChart';
import Empty from 'components/Empty';

function Activity() {
  const { t } = useTranslate();

  const [region, setRegion] = useState('CN');

  const [filterValue, setFilterValue] = useState<ActivityGlobalStatsBody>({
    activityIds: [],
    startTimeMs: dayjs().subtract(7, 'day').startOf('day').valueOf(),
    endTimeMs: dayjs().subtract(1, 'day').endOf('day').valueOf(),
  });

  const { loading, data } = useRequest(
    () => {
      return activityGlobalStats(filterValue);
    },
    {
      refreshDeps: [filterValue],
      ready: filterValue.activityIds.length > 0,
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  return (
    <Container>
      <div>
        <header>
          <h2>{t('活动数据')}</h2>
          <CurrencySelector value={region} onChange={setRegion} />
        </header>
        <Filter defaultFilterValue={filterValue} onFilterChange={setFilterValue} />
      </div>
      {!filterValue.activityIds.length ? (
        <Empty />
      ) : data ? (
        <Content data={data} filterValue={filterValue} region={region} />
      ) : loading ? (
        <LoadingOutlined />
      ) : (
        <></>
      )}
    </Container>
  );
}

function Content({
  data,
  filterValue,
  region,
}: {
  data: ActivityStatsWithDaily & {
    platformRegionData: PlatformRegionData[];
  };
  filterValue: ActivityGlobalStatsBody;
  region: string;
}) {
  const { statsLine1, chart1, statsLine2, gmvToggle, chart2, gmv, t } = useActivityContents({
    item: data,
    to: useMemo(() => dayjs(filterValue.endTimeMs), [filterValue.endTimeMs]),
    getCurrencyTotalDisplay: useCallback((list) => list.find((_) => _.region === region), [region]),
  });

  return (
    <>
      <div>
        <header>
          <h2>{t('小乐转化效果')}</h2>
        </header>
        <section>
          {statsLine1}
          <ChartContainer>
            {chart1}
            <PlatformRegionChart
              region={region}
              data={data.platformRegionData}
              field="botTradeAmountList"
            />
          </ChartContainer>
        </section>
      </div>
      <div>
        <header>
          <h2>{t('活动整体效果')}</h2>
        </header>
        <section>
          {statsLine2}
          {gmvToggle}
          <ChartContainer>
            {chart2}
            <PlatformRegionChart
              region={region}
              data={data.platformRegionData}
              field={gmv ? 'activityTradeAmountList' : 'activityTradeCount'}
            />
          </ChartContainer>
        </section>
      </div>
      <div>
        <header>
          <h2>{t('活动数据明细')}</h2>
        </header>
        <section>
          <DetailTable region={region} {...filterValue} />
        </section>
      </div>
    </>
  );
}

const Container = styled.div`
  height: 100%;
  padding-top: 16px;
  > div {
    margin-bottom: 16px;
    > header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 56px;
      padding: 0 24px;
      margin-bottom: 1px;
      background: #fff;
      > h2 {
        margin-bottom: 0;
        font-size: 16px;
      }
    }
    > section {
      padding: 16px;
      background: #fff;
      > ul {
        padding: 0;
        padding-bottom: 14px;
        border-bottom: solid 1px #eaeaea;
      }
      > span {
        padding: 0;
        margin-top: 14px;
      }
    }
  }
`;

const ChartContainer = styled.div`
  display: flex;
  align-items: stretch;
  height: 380px;
  > div {
    flex: 1 0 0;
    padding-top: 14px;
  }
  > section {
    flex: 0 0 400px;
    padding: 14px;
    border-left: solid 1px #eaeaea;
  }
`;

export default Activity;
